import { LoadingOverlay, Modal, Space } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useParams } from 'react-router-dom';
import { SmallModal } from '../../../components/SmallModal';
import { FormButtons } from '../../../components/form/FormButtons';
import { mutate } from '../../../util/api/mutate';
import { useServerDepartmentValidation } from '../hooks/useServerDepartmentValidation';
import { useUpsertDepartmentForm } from '../hooks/useUpsertDepartmentForm';
import type {
	DepartmentDto,
	UpsertDepartmentDto,
} from '../types/DepartmentDto';
import type { NewDepartmentModalProps } from './NewDepartmentModal';
import { UpsertDepartmentForm } from './UpsertDepartmentForm';
import { Title } from '../../../components/Title';

type EditDepartmentModalProps = NewDepartmentModalProps & {
	initialDepartment: UpsertDepartmentDto;
};

export function EditDepartmentModal({
	isOpened,
	close,
	triggerListUpdate,
	initialDepartment,
}: EditDepartmentModalProps) {
	const { organisation } = useParams();

	const [
		showSuccessModal,
		{ open: openSuccessModal, close: closeSuccessModal },
	] = useDisclosure(false);

	const { form, inputs } = useUpsertDepartmentForm(initialDepartment);

	const newDepartmentMutation = mutate<UpsertDepartmentDto, DepartmentDto>(
		`${organisation}/departments/edit`,
		inputs,
	);

	useServerDepartmentValidation(newDepartmentMutation, form);

	const resetAndClose = () => {
		close();
		form.reset();
		closeSuccessModal();
	};

	const updateAndClose = () => {
		resetAndClose();
		triggerListUpdate();
	};

	return (
		<>
			<Modal
				opened={isOpened}
				onClose={resetAndClose}
				title={<Title label={'Edit Department'} />}
				w={100}
				withCloseButton={true}
				size={'md'}
				centered
			>
				<LoadingOverlay visible={newDepartmentMutation.isLoading} />
				<SmallModal
					isOpen={showSuccessModal}
					close={updateAndClose}
					title={'Department Edited!'}
					body={
						'A department has been successfully edited. Click Ok to continue...'
					}
				/>
				<Space h={6} />
				<form
					onSubmit={form.onSubmit(async () => {
						await newDepartmentMutation.mutateAsync().then((value) => {
							if (value.isValid) {
								openSuccessModal();
							}
						});
					})}
				>
					<UpsertDepartmentForm form={form} />
					<FormButtons close={resetAndClose} />
				</form>
			</Modal>
		</>
	);
}
